import * as THREE from 'three'
import { GUI } from 'lil-gui'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { Water } from 'three/examples/jsm/objects/Water.js'
import { Sky } from 'three/examples/jsm/objects/Sky.js'
import { Ship } from './systems/Ship'

class Waterworld {
  scene!: THREE.Scene
  camera!: THREE.PerspectiveCamera
  renderer!: THREE.WebGLRenderer
  controls!: OrbitControls
  water!: Water
  sky!: Sky
  sun!: THREE.Vector3
  ship!: Ship
  pmremGenerator!: THREE.PMREMGenerator
  renderTarget?: THREE.WebGLRenderTarget
  hemiLight!: THREE.HemisphereLight
  sunLight!: THREE.DirectionalLight

  lastTime = performance.now()
  timeOfDay = 12
  timeScale = 10
  driftSpeed = 5
  autoDrift = true

  // camera follow in SHIP-LOCAL space (preserves zoom/orbit)
  cameraLocalOffset = new THREE.Vector3(0, 4.5, 9)
  targetLocalOffset = new THREE.Vector3(0, 1.1, -2.1) // bow focus
  userAdjustingCamera = false

  // visual water flow (north -> south) by moving water mesh + shader time
  waterFlowZ = 0

  constructor() {
    this.initScene()
    this.initRenderer()
    this.initCamera()
    this.initControls()
    this.initEnvironment()
    this.initGameObjects()
    this.initGUI()

    window.addEventListener('resize', () => this.onResize())
    this.animate()
  }

  initScene() {
    this.scene = new THREE.Scene()
    this.sun = new THREE.Vector3()

    this.hemiLight = new THREE.HemisphereLight(0xbfdfff, 0x1a2233, 0.8)
    this.scene.add(this.hemiLight)

    this.sunLight = new THREE.DirectionalLight(0xffffff, 1.1)
    this.sunLight.position.set(50, 100, 20)
    this.scene.add(this.sunLight)
  }

  initRenderer() {
    this.renderer = new THREE.WebGLRenderer({ antialias: true })
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 0.95
    document.body.appendChild(this.renderer.domElement)

    this.pmremGenerator = new THREE.PMREMGenerator(this.renderer)
    this.pmremGenerator.compileEquirectangularShader()
  }

  initCamera() {
    this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 20000)
    this.camera.position.set(0, 4.5, 9)
  }

  initControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.08
    this.controls.maxPolarAngle = Math.PI * 0.49
    this.controls.minDistance = 2
    this.controls.maxDistance = 120
    this.controls.mouseButtons = {
      LEFT: null as any,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE
    }

    this.controls.addEventListener('start', () => {
      this.userAdjustingCamera = true
    })

    this.controls.addEventListener('end', () => {
      this.userAdjustingCamera = false
      this.captureUserCameraOffsets()
    })
  }

  captureUserCameraOffsets() {
    const shipPos = this.ship.mesh.position
    const invQ = this.ship.mesh.quaternion.clone().invert()

    const worldCamOffset = this.camera.position.clone().sub(shipPos)
    this.cameraLocalOffset.copy(worldCamOffset.applyQuaternion(invQ))

    const worldTargetOffset = this.controls.target.clone().sub(shipPos)
    this.targetLocalOffset.copy(worldTargetOffset.applyQuaternion(invQ))
  }

  initEnvironment() {
    const waterGeometry = new THREE.PlaneGeometry(12000, 12000)
    this.water = new Water(waterGeometry, {
      textureWidth: 512,
      textureHeight: 512,
      waterNormals: new THREE.TextureLoader().load(
        'https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/waternormals.jpg',
        (texture) => {
          texture.wrapS = texture.wrapT = THREE.RepeatWrapping
        }
      ),
      sunDirection: new THREE.Vector3(0, 1, 0),
      sunColor: 0xffffff,
      waterColor: 0x0b3d91,
      distortionScale: 3.2
    })
    this.water.rotation.x = -Math.PI / 2
    // rotate UV orientation so dominant wave travel reads north→south
    this.water.rotation.y = Math.PI / 2
    this.scene.add(this.water)

    this.sky = new Sky()
    this.sky.scale.setScalar(10000)
    this.scene.add(this.sky)

    const u = this.sky.material.uniforms
    u['turbidity'].value = 6
    u['rayleigh'].value = 2.2
    u['mieCoefficient'].value = 0.005
    u['mieDirectionalG'].value = 0.8
    u['cloudCoverage'].value = 0.22
    u['cloudDensity'].value = 0.42
    u['cloudElevation'].value = 0.5

    this.updateSun()
  }

  initGameObjects() {
    this.ship = new Ship(this.scene)
  }

  updateSun() {
    const h = this.timeOfDay
    const day = h >= 6 && h <= 18
    const elevation = day
      ? Math.sin(((h - 6) / 12) * Math.PI) * 80
      : h > 18
        ? -10 - (h - 18) * 7
        : -10 - (6 - h) * 7

    // east (right) sunrise -> west (left) sunset
    const azimuth = 90 - (((h - 6) / 12) * 180)

    this.sun.setFromSphericalCoords(
      1,
      THREE.MathUtils.degToRad(90 - elevation),
      THREE.MathUtils.degToRad(azimuth)
    )

    this.sky.material.uniforms['sunPosition'].value.copy(this.sun)
    this.water.material.uniforms['sunDirection'].value.copy(this.sun).normalize()
    this.sunLight.position.copy(this.sun).multiplyScalar(200)
    this.sunLight.intensity = day ? 1.2 : 0.15

    // keep sky attached to main scene (do NOT reparent to temp env scene)
    // reparenting caused black world regression.
    this.scene.environment = this.renderTarget ? this.renderTarget.texture : null
    this.scene.background = day ? new THREE.Color(0x8ec5ff) : new THREE.Color(0x05070f)

    // brighter daytime
    this.renderer.toneMappingExposure = day ? 1.15 : 0.35
  }

  initGUI() {
    const gui = new GUI({ title: 'Waterworld' })
    const t = gui.addFolder('Time')
    t.add(this, 'timeOfDay', 0, 24, 0.1).name('Hour').onChange(() => this.updateSun())
    t.add(this, 'timeScale', 1, 300, 1).name('Time Speed')
    t.open()

    const m = gui.addFolder('Movement')
    m.add(this, 'autoDrift').name('Auto Drift')
    m.add(this, 'driftSpeed', 0, 20, 0.5).name('Ship Speed')
    m.open()
  }

  animate() {
    requestAnimationFrame(() => this.animate())

    const now = performance.now()
    const dt = (now - this.lastTime) / 1000
    this.lastTime = now

    // time-of-day progression
    this.timeOfDay += (dt * this.timeScale) / 3600
    if (this.timeOfDay >= 24) this.timeOfDay -= 24
    if (Math.floor(now / 1500) % 2 === 0) this.updateSun()

    // ship forward = NORTH (-Z)
    if (this.autoDrift) this.ship.mesh.position.z -= dt * this.driftSpeed * 0.35

    // water NORTH->SOUTH (toward +Z) = against ship motion
    this.waterFlowZ += dt * this.driftSpeed * 0.5
    this.water.position.z = this.waterFlowZ % 120 // visual conveyor flow
    this.water.material.uniforms['time'].value += dt * 0.35 // surface choppiness

    const t = now * 0.001
    this.ship.update(t, this.driftSpeed)

    // camera follow unless user currently rotating/zooming
    if (!this.userAdjustingCamera) {
      const shipQ = this.ship.mesh.quaternion
      const shipPos = this.ship.mesh.position

      const camWorldOffset = this.cameraLocalOffset.clone().applyQuaternion(shipQ)
      const targetWorldOffset = this.targetLocalOffset.clone().applyQuaternion(shipQ)

      const desiredCam = shipPos.clone().add(camWorldOffset)
      const desiredTarget = shipPos.clone().add(targetWorldOffset)

      this.camera.position.lerp(desiredCam, 0.08)
      this.controls.target.lerp(desiredTarget, 0.12)
    }

    this.controls.update()
    this.renderer.render(this.scene, this.camera)
  }

  onResize() {
    this.camera.aspect = window.innerWidth / window.innerHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(window.innerWidth, window.innerHeight)
  }
}

new Waterworld()
